/**
 * @file     webStorageGuard.ts
 * @purpose  Make window.sessionStorage / localStorage safe to touch before the
 *           Supabase browser client is built. In storage-denied contexts
 *           (third-party iframes, some in-app WebViews, "block all cookies")
 *           merely reading `window.sessionStorage` throws a SecurityError. The
 *           realtime socket reads it in its constructor, so the whole client
 *           failed to construct and the page died in the root error boundary.
 *           Replacing the throwing accessor with an in-memory Storage keeps the
 *           client (and the page) alive; nothing persists, which is what that
 *           context asked for anyway.
 */

function memoryStorage(): Storage {
  const data = new Map<string, string>();
  const storage = {
    get length() {
      return data.size;
    },
    clear: () => data.clear(),
    getItem: (key: string) => data.get(String(key)) ?? null,
    key: (index: number) => [...data.keys()][index] ?? null,
    removeItem: (key: string) => {
      data.delete(String(key));
    },
    setItem: (key: string, value: string) => {
      data.set(String(key), String(value));
    },
  };
  return storage as Storage;
}

function readable(target: Window, name: 'sessionStorage' | 'localStorage'): boolean {
  try {
    // The accessor itself is what throws; the value can legitimately be null.
    void target[name];
    return true;
  } catch {
    /* fire-and-forget, intentional: the throw IS the signal this probe exists to detect */
    return false;
  }
}

/**
 * Idempotent. Returns the names that had to be shimmed so callers can log or
 * test it; an empty array is the normal case.
 */
export function ensureWebStorageReadable(target: Window | undefined = typeof window === 'undefined' ? undefined : window): string[] {
  if (!target) return [];
  const shimmed: string[] = [];
  for (const name of ['sessionStorage', 'localStorage'] as const) {
    if (readable(target, name)) continue;
    try {
      Object.defineProperty(target, name, { value: memoryStorage(), configurable: true, writable: false });
      shimmed.push(name);
    } catch {
      /* fire-and-forget, intentional: a non-configurable accessor leaves the page as it was */
    }
  }
  return shimmed;
}
